import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { businessServices, subCategories as subCatData } from '../../utils/categoryData';
import './MyBusiness.css';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const statusColor = { pending: '#f59e0b', approved: '#16a34a', rejected: '#ef4444' };
const statusLabel = { pending: '⏳ Pending', approved: '✅ Approved', rejected: '❌ Rejected' };

export default function MyBusiness() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { type, biz }
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const logoRef = useRef();
  const imagesRef = useRef();

  const fetchBusinesses = () => {
    api.get('/api/businesses/my')
      .then(res => setBusinesses(res.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchBusinesses(); }, []);

  const openModal = (type, biz) => {
    setEditData({ ...biz });
    setModal({ type, biz });
  };
  const closeModal = () => { setModal(null); setEditData({}); };

  const save = async (fd) => {
    setSaving(true);
    try {
      await api.put(`/api/businesses/${modal.biz._id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchBusinesses();
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveJson = async (data) => {
    setSaving(true);
    try {
      await api.put(`/api/businesses/${modal.biz._id}`, data);
      fetchBusinesses();
      closeModal();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mybiz-page">
      <div className="container">
        <h1 className="mybiz-title">My Businesses</h1>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
        ) : businesses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏢</div>
            <p>No businesses added yet.</p>
          </div>
        ) : (
          <div className="mybiz-list">
            {businesses.map(biz => (
              <div key={biz._id} className="mybiz-card">
                <div className="mybiz-img-wrap">
                  {biz.logo
                    ? <img src={`${BASE}/${biz.logo}`} alt={biz.businessName} className="mybiz-img" />
                    : <div className="mybiz-img-placeholder">🏢</div>
                  }
                  <div className="mybiz-deleted-badge" style={{ background: statusColor[biz.status] }}>
                    {statusLabel[biz.status]}
                  </div>
                </div>

                <div className="mybiz-info">
                  <div className="mybiz-top-row">
                    <div>
                      <h2 className="mybiz-name">{biz.businessName}</h2>
                      <p className="mybiz-cats">{(biz.categories || []).join(', ') || biz.category || ''}</p>
                    </div>
                    <div className="mybiz-profile-wrap">
                      <div className="mybiz-profile-label">
                        <span>Profile</span>
                        <span className="mybiz-profile-pct">{biz.profilePct || 40}%</span>
                      </div>
                      <div className="mybiz-progress-bar">
                        <div className="mybiz-progress-fill" style={{ width: `${biz.profilePct || 40}%` }} />
                      </div>
                    </div>
                  </div>

                  <div className="mybiz-stats">
                    <div className="mybiz-stat-col">
                      <div className="mybiz-stat-item"><span className="mybiz-stat-icon">📅</span><span className="mybiz-stat-label">Established:</span><span className="mybiz-stat-val">{biz.established || 'N/A'}</span></div>
                      <div className="mybiz-stat-item"><span className="mybiz-stat-icon">📍</span><span className="mybiz-stat-label">Address:</span><span className="mybiz-stat-val">{biz.address || 'N/A'}</span></div>
                      <div className="mybiz-stat-item"><span className="mybiz-stat-icon">📍</span><span className="mybiz-stat-label">Pincode:</span><span className="mybiz-stat-val">{biz.pincode || 'N/A'}</span></div>
                    </div>
                    <div className="mybiz-stat-col">
                      <div className="mybiz-stat-item"><span className="mybiz-stat-icon">🖱️</span><span className="mybiz-stat-label">Total Clicks:</span><span className="mybiz-stat-val">{biz.totalClicks || 0}</span></div>
                      <div className="mybiz-stat-item"><span className="mybiz-stat-icon">📞</span><span className="mybiz-stat-label">Total Calls:</span><span className="mybiz-stat-val">{biz.totalCalls || 0}</span></div>
                      <div className="mybiz-stat-item"><span className="mybiz-stat-icon">💬</span><span className="mybiz-stat-label">Total Enquiries:</span><span className="mybiz-stat-val">{biz.totalEnquiries || 0}</span></div>
                    </div>
                  </div>

                  <p className="mybiz-mgmt-label">Business Management</p>
                  <div className="mybiz-actions">
                    <button className="mybiz-action-btn" onClick={() => navigate(`/business/${biz._id}`)}>👁️ Preview</button>
                    <button className="mybiz-action-btn" onClick={() => openModal('details', biz)}>✏️ Edit Details</button>
                    <button className="mybiz-action-btn" onClick={() => openModal('services', biz)}>🏛️ Edit Services</button>
                    <button className="mybiz-action-btn" onClick={() => openModal('products', biz)}>🛒 Edit Products</button>
                    <button className="mybiz-action-btn green" onClick={() => openModal('hours', biz)}>🕐 Edit Hours</button>
                    <button className="mybiz-action-btn" onClick={() => openModal('banner', biz)}>🖼️ Edit Banner</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mybiz-add-wrap">
          <button className="mybiz-add-btn" onClick={() => navigate('/add-business')}>+ Add New Business</button>
        </div>
      </div>

      {/* ── MODALS ── */}
      {modal && (
        <div className="mybiz-overlay" onClick={closeModal}>
          <div className="mybiz-modal" onClick={e => e.stopPropagation()}>
            <div className="mybiz-modal-header">
              <h3>
                {modal.type === 'details' && '✏️ Edit Business Details'}
                {modal.type === 'services' && '🏛️ Edit Services'}
                {modal.type === 'products' && '🛒 Edit Products'}
                {modal.type === 'hours' && '🕐 Edit Opening Hours'}
                {modal.type === 'banner' && '🖼️ Edit Logo & Images'}
              </h3>
              <button className="mybiz-modal-close" onClick={closeModal}>✕</button>
            </div>

            {/* ── Details Modal ── */}
            {modal.type === 'details' && (
              <DetailsModal editData={editData} setEditData={setEditData} saving={saving}
                onSave={() => saveJson({
                  businessName: editData.businessName,
                  phone: editData.phone,
                  whatsapp: editData.whatsapp,
                  email: editData.email,
                  tagline: editData.tagline,
                  description: editData.description,
                  website: editData.website,
                  established: editData.established,
                  businessType: editData.businessType,
                  pincode: editData.pincode,
                  state: editData.state,
                  district: editData.district,
                  city: editData.city,
                  address: editData.address,
                  categories: editData.categories,
                  subCategories: editData.subCategories,
                })}
              />
            )}

            {/* ── Services Modal ── */}
            {modal.type === 'services' && (
              <ServicesModal editData={editData} setEditData={setEditData} saving={saving}
                onSave={() => saveJson({ services: editData.services })}
              />
            )}

            {/* ── Products Modal ── */}
            {modal.type === 'products' && (
              <ProductsModal editData={editData} setEditData={setEditData} saving={saving}
                onSave={() => saveJson({ products: editData.products })}
              />
            )}

            {/* ── Hours Modal ── */}
            {modal.type === 'hours' && (
              <HoursModal editData={editData} setEditData={setEditData} saving={saving}
                onSave={() => saveJson({ hours: editData.hours })}
              />
            )}

            {/* ── Banner Modal ── */}
            {modal.type === 'banner' && (
              <BannerModal biz={modal.biz} saving={saving} onSave={save} logoRef={logoRef} imagesRef={imagesRef} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Details Modal ── */
function DetailsModal({ editData, setEditData, saving, onSave }) {
  const set = (k, v) => setEditData(p => ({ ...p, [k]: v }));
  const cats = editData.categories || [];
  const toggleCat = (name) => set('categories', cats.includes(name) ? cats.filter(c => c !== name) : [...cats, name]);

  return (
    <div className="mybiz-modal-body">
      <div className="mybiz-form-row">
        <div className="mybiz-form-group"><label>Business Name</label><input value={editData.businessName || ''} onChange={e => set('businessName', e.target.value)} /></div>
        <div className="mybiz-form-group"><label>Business Type</label>
          <select value={editData.businessType || ''} onChange={e => set('businessType', e.target.value)}>
            {['','Seller','Trader','Manufacturer','Manufacturer/Trader','Trader/Seller','Service Provider'].map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div className="mybiz-form-row">
        <div className="mybiz-form-group"><label>Phone</label><input value={editData.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
        <div className="mybiz-form-group"><label>WhatsApp</label><input value={editData.whatsapp || ''} onChange={e => set('whatsapp', e.target.value)} /></div>
      </div>
      <div className="mybiz-form-row">
        <div className="mybiz-form-group"><label>Email</label><input value={editData.email || ''} onChange={e => set('email', e.target.value)} /></div>
        <div className="mybiz-form-group"><label>Website</label><input value={editData.website || ''} onChange={e => set('website', e.target.value)} /></div>
      </div>
      <div className="mybiz-form-row">
        <div className="mybiz-form-group"><label>Tagline</label><input value={editData.tagline || ''} onChange={e => set('tagline', e.target.value)} /></div>
        <div className="mybiz-form-group"><label>Established Year</label><input value={editData.established || ''} onChange={e => set('established', e.target.value)} /></div>
      </div>
      <div className="mybiz-form-group"><label>Description</label><textarea rows={3} value={editData.description || ''} onChange={e => set('description', e.target.value)} /></div>
      <div className="mybiz-form-row">
        <div className="mybiz-form-group"><label>City</label><input value={editData.city || ''} onChange={e => set('city', e.target.value)} /></div>
        <div className="mybiz-form-group"><label>District</label><input value={editData.district || ''} onChange={e => set('district', e.target.value)} /></div>
      </div>
      <div className="mybiz-form-row">
        <div className="mybiz-form-group"><label>State</label><input value={editData.state || ''} onChange={e => set('state', e.target.value)} /></div>
        <div className="mybiz-form-group"><label>Pincode</label><input value={editData.pincode || ''} onChange={e => set('pincode', e.target.value)} /></div>
      </div>
      <div className="mybiz-form-group"><label>Full Address</label><input value={editData.address || ''} onChange={e => set('address', e.target.value)} /></div>
      <div className="mybiz-form-group">
        <label>Categories</label>
        <div className="mybiz-cat-grid">
          {businessServices.map((c, i) => (
            <label key={i} className="mybiz-cat-check">
              <input type="checkbox" checked={cats.includes(c.name)} onChange={() => toggleCat(c.name)} />
              {c.icon} {c.name}
            </label>
          ))}
        </div>
      </div>
      <button className="mybiz-save-btn" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
    </div>
  );
}

/* ── Services Modal ── */
function ServicesModal({ editData, setEditData, saving, onSave }) {
  const services = editData.services || [{ name: '', price: '' }];
  const setServices = (s) => setEditData(p => ({ ...p, services: s }));
  return (
    <div className="mybiz-modal-body">
      {services.map((s, i) => (
        <div key={i} className="mybiz-form-row" style={{ alignItems: 'center' }}>
          <div className="mybiz-form-group"><label>Service Name</label><input value={s.name || ''} onChange={e => setServices(services.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></div>
          <div className="mybiz-form-group"><label>Price</label><input value={s.price || ''} onChange={e => setServices(services.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} /></div>
          {services.length > 1 && <button className="mybiz-remove-btn" onClick={() => setServices(services.filter((_, j) => j !== i))}>✕</button>}
        </div>
      ))}
      <button className="mybiz-add-row-btn" onClick={() => setServices([...services, { name: '', price: '' }])}>+ Add Service</button>
      <button className="mybiz-save-btn" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Services'}</button>
    </div>
  );
}

/* ── Products Modal ── */
function ProductsModal({ editData, setEditData, saving, onSave }) {
  const products = editData.products || [{ name: '', price: '', discount: '', description: '' }];
  const setProducts = (p) => setEditData(prev => ({ ...prev, products: p }));
  return (
    <div className="mybiz-modal-body">
      {products.map((p, i) => (
        <div key={i} className="mybiz-product-card">
          <div className="mybiz-form-row">
            <div className="mybiz-form-group"><label>Product Name</label><input value={p.name || ''} onChange={e => setProducts(products.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></div>
            <div className="mybiz-form-group"><label>Price</label><input value={p.price || ''} onChange={e => setProducts(products.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} /></div>
          </div>
          <div className="mybiz-form-row">
            <div className="mybiz-form-group"><label>Discount</label><input value={p.discount || ''} onChange={e => setProducts(products.map((x, j) => j === i ? { ...x, discount: e.target.value } : x))} /></div>
            <div className="mybiz-form-group"><label>Description</label><input value={p.description || ''} onChange={e => setProducts(products.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} /></div>
          </div>
          {products.length > 1 && <button className="mybiz-remove-btn" onClick={() => setProducts(products.filter((_, j) => j !== i))}>✕ Remove</button>}
        </div>
      ))}
      <button className="mybiz-add-row-btn" onClick={() => setProducts([...products, { name: '', price: '', discount: '', description: '' }])}>+ Add Product</button>
      <button className="mybiz-save-btn" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Products'}</button>
    </div>
  );
}

/* ── Hours Modal ── */
function HoursModal({ editData, setEditData, saving, onSave }) {
  const hours = editData.hours || DAYS.reduce((a, d) => ({ ...a, [d]: { open: '09:00', close: '18:00', closed: false } }), {});
  const setHours = (h) => setEditData(p => ({ ...p, hours: h }));
  return (
    <div className="mybiz-modal-body">
      {DAYS.map(day => (
        <div key={day} className="mybiz-hours-row">
          <span className="mybiz-hours-day">{day}</span>
          <label className="mybiz-toggle-wrap">
            <input type="checkbox" checked={!hours[day]?.closed}
              onChange={e => setHours({ ...hours, [day]: { ...hours[day], closed: !e.target.checked } })} />
            <span className="mybiz-toggle-slider" />
          </label>
          {!hours[day]?.closed ? (
            <div className="mybiz-hours-times">
              <input type="time" value={hours[day]?.open || '09:00'} onChange={e => setHours({ ...hours, [day]: { ...hours[day], open: e.target.value } })} />
              <span>to</span>
              <input type="time" value={hours[day]?.close || '18:00'} onChange={e => setHours({ ...hours, [day]: { ...hours[day], close: e.target.value } })} />
            </div>
          ) : <span style={{ color: '#ef4444', fontSize: 13 }}>Closed</span>}
        </div>
      ))}
      <button className="mybiz-save-btn" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Hours'}</button>
    </div>
  );
}

/* ── Banner Modal ── */
function BannerModal({ biz, saving, onSave, logoRef, imagesRef }) {
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(biz.logo ? `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${biz.logo}` : null);
  const [newImages, setNewImages] = useState([]);

  const handleSubmit = () => {
    const fd = new FormData();
    if (logoFile) fd.append('logo', logoFile);
    newImages.forEach(f => fd.append('images', f));
    onSave(fd);
  };

  return (
    <div className="mybiz-modal-body">
      <div className="mybiz-form-group">
        <label>Business Logo</label>
        <div className="mybiz-logo-upload" onClick={() => logoRef.current.click()}>
          {logoPreview ? <img src={logoPreview} alt="logo" style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 10 }} /> : <span style={{ fontSize: 40 }}>📷</span>}
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>Click to change logo</p>
        </div>
        <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files[0]; if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); } }} />
      </div>

      <div className="mybiz-form-group">
        <label>Current Images ({(biz.images || []).length}/5)</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          {(biz.images || []).map((img, i) => (
            <img key={i} src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${img}`} alt="" style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 8 }} />
          ))}
        </div>
        {(biz.images || []).length < 5 && (
          <>
            <button className="mybiz-add-row-btn" onClick={() => imagesRef.current.click()}>+ Add More Images</button>
            <input ref={imagesRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
              onChange={e => setNewImages(Array.from(e.target.files).slice(0, 5 - (biz.images || []).length))} />
            {newImages.length > 0 && <p style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>{newImages.length} new image(s) selected</p>}
          </>
        )}
      </div>

      <button className="mybiz-save-btn" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Save Banner'}</button>
    </div>
  );
}
